import { useEffect, useMemo, useRef, useState } from "react";

import { formatBytesPretty } from "../api/drive";
import { formatRelative } from "../utils/formatRelative";
import styles from "./DuplicateResolverModal.module.css";

// Date + time-of-day, so two same-named files modified on the same day
// are still distinguishable. Drive identifies files by opaque id, not
// name+path, so a folder legitimately holds two different files with the
// same name - when they also share a modified day, the time + size are
// the only things telling them apart.
const formatModified = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// One candidate version of a duplicated filename.
export interface DuplicateCandidate {
  // Stable unique key for this candidate (the Drive file id, or a
  // synthesised key for a local / already-attached candidate).
  id: string;
  // Full folder path within the picked tree, e.g.
  // "MME1 Docs/Designs/SLD.pdf". Present for Drive-sourced candidates;
  // absent for local-disk files and already-attached uploads (no
  // folder context), which lean on `sourceLabel` instead.
  path?: string;
  // Where this version came from, shown when there's no path to
  // disambiguate by - e.g. "On your computer", "Already attached to
  // this project", "Google Drive".
  sourceLabel?: string;
  // RFC-3339 last-modified timestamp, if known.
  modifiedTime?: string;
  // Size in bytes, or null when unknown (e.g. Google-native files).
  sizeNum: number | null;
}

// One group of same-named candidates needing a pick.
export interface DuplicateGroup {
  name: string;
  candidates: DuplicateCandidate[];
  // Optional id to pre-select. The caller uses this to express intent
  // the modal can't infer - e.g. the pick-reconcile path defaults to the
  // operator's just-picked version, since comparing a picked file's
  // content mtime against an existing upload's upload time is
  // apples-to-oranges. Falls back to newest-modified when unset.
  defaultId?: string;
}

interface Props {
  open: boolean;
  groups: DuplicateGroup[];
  // Resolves with the chosen candidate id per filename. The caller
  // keeps only those and discards the rest.
  onResolve: (keepIdByName: Record<string, string>) => void;
  onCancel: () => void;
}

// Pick the newest-modified candidate as the default (the version an
// operator most likely wants). Falls back to the first when no
// timestamps are present.
const newestId = (candidates: DuplicateCandidate[]): string => {
  let best = candidates[0];
  for (const c of candidates) {
    if (!best.modifiedTime) {
      best = c;
      continue;
    }
    if (c.modifiedTime && c.modifiedTime > best.modifiedTime) best = c;
  }
  return best.id;
};

// One modal that gathers EVERY duplicated filename in a Drive import
// and lets the operator pick which version to keep for each - replacing
// the old one-window.confirm-per-duplicate flow, which was unusable for
// a folder with many repeated names. Each candidate shows its full
// path + last-modified date + size so the operator can disambiguate.
export const DuplicateResolverModal = ({
  open,
  groups,
  onResolve,
  onCancel,
}: Props) => {
  const ref = useRef<HTMLDialogElement | null>(null);
  const [selected, setSelected] = useState<Record<string, string>>({});

  // Default-select the newest version of each group whenever the set of
  // groups changes (i.e. each time the modal is opened for a new pick).
  const defaults = useMemo(() => {
    const out: Record<string, string> = {};
    for (const g of groups) out[g.name] = g.defaultId ?? newestId(g.candidates);
    return out;
  }, [groups]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setSelected(defaults);
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open, defaults]);

  const handleKeep = () => {
    // Belt-and-braces: ensure every group has a selection (defaults
    // guarantee this, but a group with no candidates would not).
    const resolved: Record<string, string> = {};
    for (const g of groups) {
      resolved[g.name] =
        selected[g.name] ?? g.defaultId ?? newestId(g.candidates);
    }
    onResolve(resolved);
  };

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
    >
      <div className={styles.body}>
        <h2 className={styles.heading}>
          Resolve duplicate filenames - {groups.length}{" "}
          {groups.length === 1 ? "name" : "names"}
        </h2>
        <p className={styles.help}>
          These names appear more than once in your selection. A project
          keeps one file per name, so pick the version to keep for each;
          the rest are skipped.
        </p>

        <div className={styles.groups}>
          {groups.map((g) => (
            <div key={g.name} className={styles.group}>
              <div className={styles.groupName} title={g.name}>
                {g.name}
              </div>
              <div className={styles.candidates}>
                {g.candidates.map((c) => {
                  const isSel = (selected[g.name] ?? defaults[g.name]) === c.id;
                  return (
                    <label
                      key={c.id}
                      className={`${styles.candidate} ${isSel ? styles.candidateSel : ""}`}
                    >
                      <input
                        type="radio"
                        name={`dup-${g.name}`}
                        className={styles.radio}
                        checked={isSel}
                        onChange={() =>
                          setSelected((prev) => ({ ...prev, [g.name]: c.id }))
                        }
                      />
                      <span className={styles.candidateBody}>
                        <span
                          className={styles.path}
                          title={c.path ?? c.sourceLabel}
                        >
                          {c.path ?? c.sourceLabel ?? "this file"}
                        </span>
                        <span className={styles.meta}>
                          {c.path && c.sourceLabel && (
                            <span>{c.sourceLabel} · </span>
                          )}
                          {c.modifiedTime ? (
                            <span>
                              modified {formatModified(c.modifiedTime)} (
                              {formatRelative(c.modifiedTime)})
                            </span>
                          ) : (
                            <span>modified date unknown</span>
                          )}
                          <span>
                            {" · "}
                            {c.sizeNum != null
                              ? formatBytesPretty(c.sizeNum)
                              : "size unknown"}
                          </span>
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.btnCancel} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnKeep}
            onClick={handleKeep}
          >
            Keep selected ({groups.length})
          </button>
        </div>
      </div>
    </dialog>
  );
};
